import { Grid } from "@mui/material";
import { DateInput } from "components/FormControls/DateInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import _ from "lodash";
import { CampaignFEValues } from "types/campaigns";
import { dayNames, monthNames } from "utilities/dateFormat";
import { useStyles } from "./styles";

export function Date({ fieldName }: { fieldName: string }) {
  const classes = useStyles();
  const { message } = useMessage();
  const { values } = useFormikContext<CampaignFEValues>();
  const { setFieldValue, setStatus, status } = useFormikContext();
  const value = _.get(values, fieldName);

  return (
    <>
      <Grid item xs={6}>
        <SelectInput
          className={classes.input}
          data-test-id="condition-operator"
          items={[
            { id: "is_after", label: message({ id: "is_after" }) },
            { id: "is_before", label: message({ id: "is_before" }) },
            { id: "is_between", label: message({ id: "is_between" }) },
            {
              id: "is_not_between",
              label: message({ id: "is_not_between" }),
            },
            {
              id: "is_day_of_week",
              label: message({ id: "is_day_of_week" }),
            },
            {
              id: "is_month_of_year",
              label: message({ id: "is_month_of_year" }),
            },
          ]}
          label={<RequiredFieldLabel text={message({ id: "condition" })} />}
          name={`${fieldName}.operator`}
          onChange={(event) => {
            let newValue: any;

            if (
              ["is_between", "is_not_between"].includes(
                event.target.value as string,
              )
            ) {
              newValue = { from: null, to: null };
            } else if (
              ["is_day_of_week", "is_month_of_year"].includes(
                event.target.value as string,
              )
            ) {
              newValue = [];
            } else {
              newValue = null;
            }

            setFieldValue(`${fieldName}.data`, newValue);

            if (_.get(status, fieldName)) {
              const newStatus = _.set(status, fieldName, {});

              setStatus(newStatus);
            }
          }}
        />
      </Grid>
      {["is_after", "is_before"].indexOf(value.operator) >= 0 ? (
        <Grid item xs={12}>
          <DateInput
            className={classes.input}
            fullWidth
            data-test-id="condition-value"
            label={<RequiredFieldLabel text={message({ id: "date" })} />}
            name={`${fieldName}.data`}
            shouldAddInitialHourOfDay
          />
        </Grid>
      ) : ["is_between", "is_not_between"].indexOf(value.operator) >= 0 ? (
        <>
          <Grid item xs={6}>
            <DateInput
              className={classes.input}
              data-test-id="condition-value-from"
              label={<RequiredFieldLabel text={message({ id: "dateFrom" })} />}
              name={`${fieldName}.data.from`}
              shouldAddInitialHourOfDay
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <DateInput
              className={classes.input}
              data-test-id="condition-value-to"
              label={<RequiredFieldLabel text={message({ id: "dateTo" })} />}
              name={`${fieldName}.data.to`}
              shouldAddFinalHourOfDay
            />
          </Grid>
        </>
      ) : value.operator === "is_day_of_week" ? (
        <Grid item xs={12}>
          <SelectInput
            multiple
            displaySearch
            data-test-id="condition-value"
            width="100%"
            items={dayNames.map((day: string) => ({
              label: _.capitalize(message({ id: day })),
              id: day,
            }))}
            label={message({ id: "day" })}
            name={`${fieldName}.data`}
            placeholder={message({ id: "day" })}
          />
        </Grid>
      ) : value.operator === "is_month_of_year" ? (
        <Grid item xs={12}>
          <SelectInput
            multiple
            displaySearch
            data-test-id="condition-value"
            width="100%"
            items={monthNames.map((month: string) => ({
              label: _.capitalize(message({ id: month })),
              id: month,
            }))}
            label={message({ id: "month" })}
            name={`${fieldName}.data`}
            placeholder={message({ id: "month" })}
          />
        </Grid>
      ) : null}
    </>
  );
}
